import {
  ExperiencesAvailabilityRequest,
  ExperiencesAvailabilityRequestEnum,
  ExperiencesAvailabilityResponse,
  ILocationQueryLabel,
  LocationQueryEnum,
  IResult,
  ICategorizedResponse,
  RatingRefinement,
  TripadvisorRatingRefinement,
  ITrackingProperties,
  ActivitiesEntryTypeEnum,
  CODE_TO_ENTRY_TYPE,
  ACTIVITIES_ENTRY,
  ActivitiesAvailabilityProperties,
  ExperienceTag,
  ActivityTagKindValue,
} from "redmond";
import dayjs from "dayjs";
import * as H from "history";
import queryStringParser from "query-string";
import { call, delay, fork, put, putResolve, select } from "redux-saga/effects";
import { fetchRecentlySearchedExperiences } from "b2b-base/src/api/v1/recently-viewed-and-searched/fetchRecentlySearchedExperiences";
import { getBothSelections, getSelectionId } from "../../../utils/selectionHelpers";

import { IStoreState } from "../../../reducers/types";
import { actions } from "../actions";
import { fetchExperiencesAvailability } from "../../../api/v0/availability/fetchExperiencesAvailability";
import Logger from "../../../utils/logger";
import {
  getCategoryTagId,
  getEntryType,
  getFromDate,
  getLocation,
  getUntilDate,
} from "../../search/reducer";
import {
  getExperiencesAvailabilityNextPageToken,
  getFilteredDurationTimes,
  getFilteredPopularFeatures,
  getFilteredStartTimes,
  getFilteredTags,
  getFilteredTripAdvisorRating,
  getFilterPrice,
  getSortOption,
  IExperiencesAvailabilityFilterState,
  getExperiencesMaxPrice,
  getActivitiesAvailabilityProperties,
} from "../reducer";
import { actions as searchActions } from "../../search/actions";
import { fetchLocationAutocomplete } from "../../../api/v0/search/fetchLocationAutocomplete";
import { fetchCombinedAutocomplete as fetchCombinedAutocompleteApi } from "../../../api/v0/search/fetchCombinedAutocomplete";
import { CombinedAutocompleteResponse } from "../../search/types";
import { PATH_HOME } from "../../../utils/paths";
import { trackEvent } from "../../../api/v0/analytics/trackEvent";

/**
 * Ensures a tag has the proper `kind` field in the discriminated union format
 * expected by the backend: { ActivityTagKind: "Category" | "Attraction" }
 */
const formatTagWithKind = (
  tag: ExperienceTag,
  defaultKind: ActivityTagKindValue,
): ExperienceTag => ({
  ...tag,
  kind: tag.kind ?? { ActivityTagKind: defaultKind },
});

const DEST_NAME_CACHE_KEY = "dest_name_cache";
const DEST_NAME_CACHE_MAX_ENTRIES = 50;

function getDestNameCache(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(DEST_NAME_CACHE_KEY) || "{}");
  } catch {
    return {};
  }
}

function setDestNameCache(destId: string, name: string): void {
  try {
    const cache = getDestNameCache();
    cache[destId] = name;
    // Evict oldest entries if cache exceeds max size
    const keys = Object.keys(cache);
    if (keys.length > DEST_NAME_CACHE_MAX_ENTRIES) {
      keys.slice(0, keys.length - DEST_NAME_CACHE_MAX_ENTRIES).forEach((k) => delete cache[k]);
    }
    localStorage.setItem(DEST_NAME_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore localStorage errors
  }
}

function* refreshRecentlySearched() {
  yield delay(2000);
  try {
    const resp: { searches?: unknown[] } = yield call(fetchRecentlySearchedExperiences);
    if (resp?.searches && resp.searches.length > 0) {
      localStorage.setItem(
        "recently_searched_experiences",
        JSON.stringify(resp.searches),
      );
      window.dispatchEvent(new Event("update_recently_searched_experiences"));
    }
  } catch {
    // Non-critical
  }
}

export function* fetchExperiencesAvailabilitySaga(
  action: actions.IFetchExperiencesAvailability,
) {
  try {
    let requestBody: ExperiencesAvailabilityRequest | undefined;
    const state: IStoreState = yield select();

    const requestParams: {
      requestData: {
        fromDate: Date | null;
        untilDate: Date | null;
        locationToSearch: IResult | null;
        categoryTagId: string | null;
        categoryTagKind: ActivityTagKindValue | null;
      };
      entryType?: string | null;
      // eslint-disable-next-line no-use-before-define -- saga defined below
    } = yield call(getAvailabilityRequestParametersSaga, action);

    const { requestData } = requestParams;

    if (!requestData) {
      // shouldRedirect already pushed to HOME — unmount cleanup resets call state.
      // Don't set Failed here: it triggers the error modal unnecessarily.
      return;
    }
    const {
      fromDate,
      untilDate,
      locationToSearch,
      categoryTagId,
      categoryTagKind,
    } = requestData;

    const maxPrice = getExperiencesMaxPrice(state);

    // TODO: Break these to helper functions
    const refinements: IExperiencesAvailabilityFilterState = {
      tripAdvisorRating: getFilteredTripAdvisorRating(state),
      duration: getFilteredDurationTimes(state),
      startTime: getFilteredStartTimes(state),
      price: getFilterPrice(state),
      tags: getFilteredTags(state),
      popularFeatures: getFilteredPopularFeatures(state),
      sort: getSortOption(state),
    };

    const ratingRefinement: RatingRefinement = { minTripadvisorRating: 0 };
    switch (refinements.tripAdvisorRating) {
      case TripadvisorRatingRefinement.Good:
        ratingRefinement.minTripadvisorRating = 3.5;
        break;
      case TripadvisorRatingRefinement.VeryGood:
        ratingRefinement.minTripadvisorRating = 4;
        break;
      case TripadvisorRatingRefinement.Great:
        ratingRefinement.minTripadvisorRating = 4.5;
        break;
      default:
        ratingRefinement.minTripadvisorRating = 3;
        break;
    }

    switch (action.requestType) {
      case ExperiencesAvailabilityRequestEnum.InitialSearch:
      case ExperiencesAvailabilityRequestEnum.RequestParams: {
        if (JSON.stringify(requestData) === "{}") return;
        if (!locationToSearch?.id) return;

        const { experiencesSelection, activitiesSelection } = getBothSelections(
          locationToSearch.id,
        );

        // Merge enrichment data from combined search result if available.
        // The label override ensures recently searched shows the suggestion name
        // (e.g., "Colosseum", "Food & Drink") instead of the destination name.
        // Navigation for non-destination items is handled via destinationId in the URL.
        let enrichedExperiencesSelection = experiencesSelection;
        let enrichedActivitiesSelection = activitiesSelection;
        try {
          const enrichmentJson = localStorage.getItem(
            "combined_search_enrichment",
          );
          if (enrichmentJson) {
            const enrichment = JSON.parse(enrichmentJson);
            enrichedExperiencesSelection = {
              ...experiencesSelection,
              ...(enrichment.label ? { name: enrichment.label } : {}),
              thumbnailUrl: enrichment.thumbnailUrl,
              activityCount: enrichment.activityCount,
              suggestionType: enrichment.suggestionType,
            };
            enrichedActivitiesSelection = {
              ...activitiesSelection,
              ...(enrichment.label ? { name: enrichment.label } : {}),
              thumbnailUrl: enrichment.thumbnailUrl,
              activityCount: enrichment.activityCount,
              suggestionType: enrichment.suggestionType,
            };
            localStorage.removeItem("combined_search_enrichment");
          }
        } catch {
          // Ignore parse errors
        }

        requestBody = {
          AvailabilityRequest: ExperiencesAvailabilityRequestEnum.InitialSearch,
          // Omit dateRange for dateless requests — API returns all upcoming availability
          ...(fromDate && untilDate
            ? {
              dateRange: {
                from: dayjs(fromDate).format("YYYY-MM-DD"),
                until: dayjs(untilDate).format("YYYY-MM-DD"),
              },
            }
            : {}),
          experiencesSelection: enrichedExperiencesSelection,
          activitiesSelection: enrichedActivitiesSelection,
          refinement: {
            sort: refinements.sort,
            tags: [
              // Category/attraction tags from filter - preserve existing kind or default to category
              ...(refinements.tags ?? []).map((tag) =>
                formatTagWithKind(tag, "Category"),
              ),
              // Fallback category/attraction tag from URL param
              ...(categoryTagId &&
                !state.experiencesAvailability.selectedGroupedExperience
                ? [
                  {
                    id: parseInt(categoryTagId, 10),
                    name: "",
                    kind: {
                      ActivityTagKind: (categoryTagKind ??
                        "Category") as ActivityTagKindValue,
                    },
                  },
                ]
                : []),
              ...(refinements.popularFeatures ?? []),
            ],
            duration: refinements.duration ? refinements.duration : [],
            startTime: refinements.startTime ? refinements.startTime : [],
            price:
              // for when use selects max price
              maxPrice?.fiat.value !== refinements.price?.maxPriceUSD
                ? refinements.price
                : undefined,
            rating: ratingRefinement,
          },
        };

        break;
      }
      case ExperiencesAvailabilityRequestEnum.FollowupSearch: {
        const nextPageToken = getExperiencesAvailabilityNextPageToken(state);

        if (!nextPageToken) return;

        requestBody = {
          nextPageToken,
          AvailabilityRequest:
            ExperiencesAvailabilityRequestEnum.FollowupSearch,
        };

        break;
      }
      default:
        return; // Exit if no matching request type
    }

    if (!requestBody) {
      return; // Exit if requestBody wasn't assigned
    }

    const startTime = Date.now();
    const availabilityResponse: ExperiencesAvailabilityResponse =
      yield fetchExperiencesAvailability(requestBody);

    const runtimeMs = Date.now() - startTime;
    yield put(
      actions.setExperiencesAvailabilityResults({
        payload: availabilityResponse,
        request: requestBody,
        responseType: action.requestType,
        resetAvailabilityResponse: action.resetAvailabilityResponse,
        resetPriceFilterConstraints: action.resetPriceFilterConstraints,
      }),
    );

    const properties: ITrackingProperties<ActivitiesAvailabilityProperties> =
      yield select(getActivitiesAvailabilityProperties);

    if (
      action.requestType === ExperiencesAvailabilityRequestEnum.InitialSearch
    ) {
      trackEvent({
        eventName: ACTIVITIES_ENTRY,
        properties: {
          ...properties.properties,
          runtime_ms: runtimeMs,
        },
        encryptedProperties: properties.encryptedProperties,
      });

      // Refresh recently searched data after a successful initial search
      // so the landing page shows the latest searches without a full page reload.
      // Forked so it doesn't block and is cancelled if the saga is cancelled.
      yield fork(refreshRecentlySearched);
    }
  } catch (e) {
    if (
      action.requestType === ExperiencesAvailabilityRequestEnum.InitialSearch
    ) {
      yield put(actions.setExperiencesAvailabilityCallStateFailed());
    }
    Logger.debug(e);
  }
}

const getExistingStateVariables = (state: IStoreState) => ({
  fromDate: getFromDate(state),
  untilDate: getUntilDate(state),
  location: getLocation(state),
  categoryTagId: getCategoryTagId(state),
  entryType: getEntryType(state),
});

export const shouldRedirect = ({
  fromDate,
  locationToSearch,
  isDatelessEnabled,
}: {
  fromDate: Date | null;
  untilDate: Date | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- IResult type not available in saga scope
  locationToSearch: any;
  isDatelessEnabled?: boolean;
}) => {
  if (!locationToSearch) return true;
  if (!fromDate && !isDatelessEnabled) return true;
  return false;
};

export interface IExperiencesAvailabilityParsedQuery {
  fromDate: Date | null;
  untilDate: Date | null;
  locationName: string | null;
  destinationId: string | null;
  categoryTagId: number | null;
  categoryTagKind: ActivityTagKindValue | null;
  entryType: ActivitiesEntryTypeEnum | null;
  entryTypesAll: ActivitiesEntryTypeEnum[];
  lodgingId: string | null;
  staysAdvance: number | null;
}

const parseQueryString = (
  history: H.History,
): IExperiencesAvailabilityParsedQuery => {
  const queryString = history?.location?.search || "";

  const {
    fromDate,
    untilDate,
    locationName,
    destinationId,
    categoryTagId,
    entryType,
    lodgingId,
    staysAdvance,
  } = queryStringParser.parse(queryString);

  // Parse entry type chain from sessionStorage (preferred, has full chain)
  // or URL param (fallback). sessionStorage is written by the b2bportal
  // combined search and consumed once here.
  let entryTypeRaw: string | null = null;
  try {
    const stored = sessionStorage.getItem("entry_types_all");
    if (stored != null) {
      entryTypeRaw = stored;
      sessionStorage.removeItem("entry_types_all");
    }
  } catch { /* non-critical */ }
  if (entryTypeRaw == null && typeof entryType === "string") {
    entryTypeRaw = entryType;
  }
  const parsedEntryTypesAll: ActivitiesEntryTypeEnum[] =
    entryTypeRaw != null
      ? entryTypeRaw
        .split("_")
        .map((code) => CODE_TO_ENTRY_TYPE[code])
        .filter((t): t is ActivitiesEntryTypeEnum => t != null)
      : [];
  const parsedEntryType = parsedEntryTypesAll.length > 0
    ? parsedEntryTypesAll[parsedEntryTypesAll.length - 1]
    : null;

  // Parse categoryTagId which may include kind suffix: "id_0" for Category, "id_1" for Attraction
  let parsedCategoryTagId: number | null = null;
  let parsedCategoryTagKind: ActivityTagKindValue | null = null;

  if (categoryTagId) {
    const tagIdStr = categoryTagId as string;
    const underscoreIndex = tagIdStr.lastIndexOf("_");

    if (underscoreIndex > 0 && underscoreIndex < tagIdStr.length - 1) {
      // Has kind suffix
      const idPart = tagIdStr.substring(0, underscoreIndex);
      const kindPart = tagIdStr.substring(underscoreIndex + 1);

      parsedCategoryTagId = parseInt(idPart, 10);
      parsedCategoryTagKind = kindPart === "1" ? "Attraction" : "Category";
    } else {
      // No kind suffix, default to Category for backwards compatibility
      parsedCategoryTagId = parseInt(tagIdStr, 10);
      parsedCategoryTagKind = "Category";
    }
  }

  return {
    fromDate: fromDate ? dayjs(fromDate as string).toDate() : null,
    untilDate: untilDate ? dayjs(untilDate as string).toDate() : null,
    locationName: locationName as string,
    destinationId: destinationId ? (destinationId as string) : null,
    categoryTagId: parsedCategoryTagId,
    categoryTagKind: parsedCategoryTagKind,
    entryType: parsedEntryType,
    entryTypesAll: parsedEntryTypesAll,
    lodgingId: lodgingId ? (lodgingId as string) : null,
    staysAdvance: staysAdvance ? parseInt(staysAdvance as string, 10) : null,
  };
};

function* fetchLocation(
  parsedQueryString: IExperiencesAvailabilityParsedQuery,
) {
  if (!parsedQueryString.locationName)
    return { correspondingLocation: undefined };

  const location = decodeURIComponent(parsedQueryString.locationName);

  const requestBody: ILocationQueryLabel = {
    LocationQuery: LocationQueryEnum.Label,
    l: location,
  };

  const { categories } = yield fetchLocationAutocomplete(requestBody);
  yield put(searchActions.setLocationCategories(categories));

  // Get all results from all categories
  const allResults = categories.flatMap(
    (category: ICategorizedResponse) => category.results,
  );

  // First, try to find an exact match
  const exactMatch = allResults.find(
    (result: IResult) => result.label.toLowerCase() === location.toLowerCase(),
  );

  // If no exact match, take the first result (autocomplete already did fuzzy matching)
  let correspondingLocation = exactMatch ?? allResults[0] ?? null;

  // If name-based lookup failed and we have a destinationId (e.g., recently searched
  // category/attraction where locationLabel is the category name, not the destination),
  // resolve the destination name from cache or via combined autocomplete.
  if (!correspondingLocation && parsedQueryString.destinationId) {
    let destName: string | null = null;

    // Strategy 1: Check localStorage cache (populated on every availability visit)
    destName = getDestNameCache()[parsedQueryString.destinationId] ?? null;

    // Strategy 2: Use combined autocomplete scoped to the destination ID —
    // product/attraction results include destinationName
    if (!destName) {
      try {
        const combinedResp: CombinedAutocompleteResponse = yield call(fetchCombinedAutocompleteApi, {
          query: location,
          destinationId: parsedQueryString.destinationId,
        });
        destName = combinedResp.groups
          .flatMap((g) => g.suggestions)
          .find((s) => s.destinationName)?.destinationName ?? null;
      } catch {
        // Ignore
      }
    }

    if (destName) {
      try {
        const retryBody: ILocationQueryLabel = {
          LocationQuery: LocationQueryEnum.Label,
          l: destName,
        };
        const retryResult: { categories: ICategorizedResponse[] } =
          yield fetchLocationAutocomplete(retryBody);
        yield put(searchActions.setLocationCategories(retryResult.categories));
        const retryResults = retryResult.categories.flatMap(
          (category: ICategorizedResponse) => category.results
        );
        correspondingLocation = retryResults[0] ?? null;
      } catch {
        // Ignore
      }
    }
  }

  return { correspondingLocation };
}

export function* getAvailabilityRequestParametersSaga({
  history,
  isDatelessEnabled,
}: actions.IFetchExperiencesAvailability) {
  // Bail out if we're no longer on the availability page. This prevents
  // a race condition where unmount cleanup resets call state to NotCalled,
  // re-triggering this saga against the wrong URL (e.g., /activities/shop/).
  if (!history.location.pathname.includes("/availability")) {
    return {};
  }

  const state: IStoreState = yield select();

  let { fromDate, untilDate, location, categoryTagId, entryType } =
    getExistingStateVariables(state);

  const parsedQueryString = parseQueryString(history);

  if (!location || location?.label !== parsedQueryString.locationName) {
    const { correspondingLocation } = yield fetchLocation(parsedQueryString);
    location = correspondingLocation;
    yield put(searchActions.setLocation(location));

    // Cache destination ID → name for recently searched resolution
    if (location?.id && location?.label) {
      try {
        const destId = getSelectionId(location.id);
        if (destId) {
          setDestNameCache(destId, location.label);
        }
      } catch {
        // Ignore
      }
    }
  }

  // Compare dates by date-only (YYYY-MM-DD) to avoid time component mismatches
  // that can occur when dates from the calendar picker have different times
  // than dates parsed from the URL (which dayjs sets to midnight)
  const fromDateStr = fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : null;
  const parsedFromDateStr = parsedQueryString.fromDate
    ? dayjs(parsedQueryString.fromDate).format("YYYY-MM-DD")
    : null;

  if (!fromDate || fromDateStr !== parsedFromDateStr) {
    fromDate = parsedQueryString.fromDate;
    yield putResolve(searchActions.setFromDate(fromDate));
  }

  const untilDateStr = untilDate ? dayjs(untilDate).format("YYYY-MM-DD") : null;
  const parsedUntilDateStr = parsedQueryString.untilDate
    ? dayjs(parsedQueryString.untilDate).format("YYYY-MM-DD")
    : null;

  if (!untilDate || untilDateStr !== parsedUntilDateStr) {
    untilDate = parsedQueryString.untilDate;
    yield putResolve(searchActions.setUntilDate(untilDate));
  }

  if (!categoryTagId || categoryTagId !== parsedQueryString.categoryTagId) {
    categoryTagId = parsedQueryString.categoryTagId;
    yield put(
      searchActions.setCategoryTagId(
        categoryTagId,
        parsedQueryString.categoryTagKind,
      ),
    );

    // Fetch attraction details if the tag is an attraction
    if (categoryTagId && parsedQueryString.categoryTagKind === "Attraction") {
      yield put(actions.fetchAttraction(categoryTagId));
    }
  }

  const currentChain: ActivitiesEntryTypeEnum[] = yield select(
    (s: IStoreState) => s.experiencesSearch.entryTypesAll ?? []
  );
  const shouldUpdateChain =
    parsedQueryString.entryTypesAll.length > 0 &&
    parsedQueryString.entryTypesAll.length >= currentChain.length;

  if (shouldUpdateChain) {
    yield put(searchActions.setEntryTypesAll(parsedQueryString.entryTypesAll));
  }
  if (
    parsedQueryString.entryType &&
    parsedQueryString.entryType !== entryType
  ) {
    entryType = parsedQueryString.entryType;
    yield put(searchActions.setEntryType(parsedQueryString.entryType));
  }

  if (parsedQueryString.lodgingId) {
    yield put(searchActions.setLodgingId(parsedQueryString.lodgingId));
  }

  if (parsedQueryString.staysAdvance !== null) {
    yield put(searchActions.setStaysAdvance(parsedQueryString.staysAdvance));
  }

  if (
    shouldRedirect({
      fromDate,
      untilDate,
      locationToSearch: location,
      isDatelessEnabled,
    })
  ) {
    history.push(PATH_HOME);
    return {};
  }

  return {
    requestData: {
      fromDate,
      untilDate,
      locationToSearch: location,
      categoryTagId,
      categoryTagKind: parsedQueryString.categoryTagKind,
    },
    entryType,
  };
}
